import { useContext, useState, useRef } from "react";
import { YearContext, AuthContext } from "../../context/context";
import {
  TbFileText,
  TbEdit,
  TbCirclePlus,
  TbTrash,
  TbEye,
  TbLock,
  TbPlus,
} from "react-icons/tb";
import { submitLetter, deleteLetter, updateLetter } from "../../api";
import InputText from "../Inputs/InputText";
import InputTextarea from "../Inputs/InputTextarea";
import animation from "../../Animations/letter-animation.json";
import animation2 from "../../Animations/write-letter-animation.json";
import BtnSecondaryM from "../Buttons/BtnSecondaryM";
import Lottie from "lottie-react";
import Popup from "../Elements/Popup";
import Card from "./Card";
import LetterItem from "../Elements/LetterItem";
import { useLanguage } from "../../context/LanguageContext";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Toast from "../Elements/Toast";
import { differenceInSeconds, endOfYear, isBefore } from "date-fns";
import CardMobile from "./CardMobile";
import useIsMobile from "../../hooks/useIsMobile";

const letterSchema = z.object({
  title_letter: z
    .string()
    .min(2, "Title must be at least 2 characters")
    .max(200),
  letter: z.string().min(2, "Letter must be at least 2 characters").max(5000),
});

const CardLetter = () => {
  const { selectedYear } = useContext(YearContext);
  const { userData, setUserData } = useContext(AuthContext);
  const { copy } = useLanguage();

  const [popupType, setPopupType] = useState(null);
  const [selectedLetter, setSelectedLetter] = useState(null);
  const [letterToEdit, setLetterToEdit] = useState(null);
  const [editedLetterTitle, setEditedLetterTitle] = useState("");
  const [editedLetterText, setEditedLetterText] = useState("");
  const [editErrors, setEditErrors] = useState({
    title_letter: "",
    letter: "",
  });
  const [letterToDelete, setLetterToDelete] = useState(null);
  const [toast, setToast] = useState({ message: "", type: "success" });

  const lottieRef = useRef(null);
  const formRef = useRef(null);
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(letterSchema),
    defaultValues: {
      title_letter: "",
      letter: "",
    },
  });

  const isMobile = useIsMobile();

  const openPopup = (type) => setPopupType(type);
  const closePopup = () => {
    reset();
    setSelectedLetter(null);
    setLetterToEdit(null);
    setEditedLetterTitle("");
    setEditedLetterText("");
    setEditErrors({ title_letter: "", letter: "" });
    setPopupType(null);
  };

  const handleSubmitLetter = async (data) => {
    try {
      const newLetter = await submitLetter(
        data.letter,
        data.title_letter,
        userData.id,
        selectedYear
      );
      if (!newLetter) {
        setToast({
          message: copy.card_letter.errors.submit_failed,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      newUserData.letters.push(newLetter);
      setUserData(newUserData);

      reset();
      setToast({
        message: copy.card_letter.errors.submit_success,
        type: "success",
      });
      closePopup();
    } catch (err) {
      console.error("Error submitting letter:", err.message);
      setToast({
        message: copy.card_letter.errors.submit_error,
        type: "error",
      });
    }
  };

  const letterData = selectedYear
    ? userData?.letters?.filter((item) => item.year === selectedYear)
    : userData?.letters;

  const handleDeleteClickLetter = (letterId) => {
    setLetterToDelete(letterId);
    openPopup("letter-delete");
  };

  const handleDeleteLetter = async () => {
    if (!letterToDelete) {
      setToast({ message: copy.card_letter.errors.not_found, type: "error" });
      return;
    }

    const result = await deleteLetter(letterToDelete);
    if (!result) {
      setToast({
        message: copy.card_letter.errors.delete_failed,
        type: "error",
      });
      return;
    }

    const newUserData = {
      ...userData,
      letters: userData.letters.filter(
        (letter) => letter.id !== letterToDelete
      ),
    };
    setUserData(newUserData);

    setToast({
      message: copy.card_letter.errors.delete_success,
      type: "success",
    });
    setLetterToDelete(null);
    closePopup();
  };

  const handleReadClickLetter = (letterItem) => {
    setSelectedLetter(letterItem);
    openPopup("written-letter");
  };

  const handleEditClickLetter = (letterItem) => {
    if (!letterItem) return;
    setLetterToEdit(letterItem.id);
    setEditedLetterTitle(letterItem.title_letter);
    setEditedLetterText(letterItem.letter);
    setEditErrors({ title_letter: "", letter: "" });
    openPopup("letter-edit");
  };

  const handleEditLetter = async () => {
    const result = letterSchema.safeParse({
      title_letter: editedLetterTitle,
      letter: editedLetterText,
    });

    if (!result.success) {
      const formatted = result.error.format();
      setEditErrors({
        title_letter: formatted.title_letter?._errors?.[0] || "",
        letter: formatted.letter?._errors?.[0] || "",
      });
      return;
    }

    try {
      const updatedLetter = await updateLetter(letterToEdit, {
        title_letter: editedLetterTitle,
        letter: editedLetterText,
      });

      if (!updatedLetter) {
        setToast({
          message: copy.card_letter.errors.update_failed,
          type: "error",
        });
        return;
      }

      const newUserData = { ...userData };
      newUserData.letters = newUserData.letters.map((l) =>
        l.id === letterToEdit
          ? {
              ...l,
              letter: updatedLetter.letter,
              title_letter: updatedLetter.title_letter,
            }
          : l
      );

      setUserData(newUserData);
      setToast({
        message: copy.card_letter.errors.update_success,
        type: "success",
      });
      closePopup();
    } catch (err) {
      console.error("Error updating letter:", err.message);
      setToast({
        message: copy.card_letter.errors.update_failed,
        type: "error",
      });
    }
  };

  const addLetterDropdown = [
    {
      text: copy.dropdown.add,
      onClick: () => openPopup("letter"),
      icon: <TbCirclePlus />,
    },
  ];

  const letterDropdown = (letterItem, isLocked) =>
    isLocked
      ? [
          {
            text: copy.dropdown.delete,
            onClick: () => handleDeleteClickLetter(letterItem.id),
            icon: <TbTrash />,
          },
        ]
      : [
          {
            text: copy.dropdown.view,
            onClick: () => handleReadClickLetter(letterItem),
            icon: <TbEye />,
          },
          {
            text: copy.dropdown.edit,
            onClick: () => handleEditClickLetter(letterItem),
            icon: <TbEdit />,
          },
          {
            text: copy.dropdown.delete,
            onClick: () => handleDeleteClickLetter(letterItem.id),
            icon: <TbTrash />,
          },
        ];

  const handleMouseEnter = () => {
    if (lottieRef.current && !isAnimationPlaying) {
      lottieRef.current.play();
      setIsAnimationPlaying(true);
    }
  };

  const handleAnimationComplete = () => {
    setIsAnimationPlaying(false);
  };

  const getUnlockDate = (year) => endOfYear(new Date(year, 0, 1));
  const getCountdown = (unlockDate) => {
    const now = new Date();
    let diff = Math.max(0, differenceInSeconds(unlockDate, now));
    const days = Math.floor(diff / (3600 * 24));
    diff -= days * 3600 * 24;
    const hours = Math.floor(diff / 3600);
    diff -= hours * 3600;
    const minutes = Math.floor(diff / 60);
    const seconds = diff - minutes * 60;
    return { days, hours, minutes, seconds };
  };

  return (
    <>
      {toast.message && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ message: "", type: "success" })}
        />
      )}

      {/* Mobile Card */}
      {isMobile &&
        (letterData && letterData.length > 0 ? (
          (() => {
            const letter = letterData[0];
            const unlockDate = getUnlockDate(letter.year);
            const now = new Date();
            const isLocked = isBefore(now, unlockDate);
            const countdown = getCountdown(unlockDate);
            if (isLocked) {
              return (
                <CardMobile
                  title={copy.card_letter.headline}
                  dropdownOptions={letterDropdown(letter, true)}
                  contentBgColor="bg-blue-100"
                >
                  <div className="py-4">
                    <div className="flex flex-col items-center justify-center w-full h-full">
                      <BtnSecondaryM
                        buttonCTA={`Open in: ${countdown.days}d ${countdown.hours}h ${countdown.minutes}m`}
                        disabled={true}
                        className="mt-2"
                      />
                    </div>
                  </div>
                </CardMobile>
              );
            } else {
              return (
                <CardMobile
                  title={copy.card_letter.headline}
                  dropdownOptions={letterDropdown(letter, false)}
                >
                  <div className="py-4">
                    <div className="w-full text-center text-base mt-2 mb-1 font-semibold">
                      {letter.title_letter}
                    </div>
                    <div className="w-full text-center text-sm italic line-clamp-2">
                      {letter.letter}
                    </div>
                  </div>
                </CardMobile>
              );
            }
          })()
        ) : (
          <CardMobile
            title={copy.card_letter.headline}
            dropdownOptions={addLetterDropdown}
            contentBgColor="bg-blue-100"
          >
            <div className="py-4">
              <div className="w-full text-center text-base mt-2 mb-1">
                {copy.card_letter.fallback_text}
              </div>
              <button
                className="w-10 h-10 rounded-md flex items-center justify-center shadow-sm bg-white card-base-color transition-colors duration-200 mt-2 mx-auto"
                onClick={() => openPopup("letter")}
                aria-label={copy.dropdown.add}
              >
                <TbPlus className="text-2xl" />
              </button>
            </div>
          </CardMobile>
        ))}

      {/* Desktop Card(s) */}
      {!isMobile &&
        (letterData && letterData.length > 0 ? (
          letterData.map((letterItem) => {
            const unlockDate = getUnlockDate(letterItem.year);
            const now = new Date();
            const isLocked = isBefore(now, unlockDate);
            const countdown = getCountdown(unlockDate);
            return (
              <Card
                key={letterItem.id}
                iconTop={isLocked ? TbLock : TbFileText}
                title={copy.card_letter.headline}
                description={`${copy.card_letter.description} ${selectedYear}`}
                options={letterDropdown(letterItem, isLocked)}
                className="justify-center"
                cardClassName="h-full"
              >
                <div className="relative h-full flex flex-col items-center justify-center gap-2 p-4">
                  <div
                    className="w-full h-[130px] overflow-hidden relative"
                    onMouseEnter={handleMouseEnter}
                  >
                    <Lottie
                      lottieRef={lottieRef}
                      animationData={animation}
                      loop={false}
                      autoplay={false}
                      className="object-cover w-full h-[105%]"
                      onComplete={handleAnimationComplete}
                    />
                  </div>
                  {isLocked ? (
                    <BtnSecondaryM
                      buttonCTA={`${copy.card_letter.open_button} ${countdown.days}d ${countdown.hours}h ${countdown.minutes}m`}
                      disabled={true}
                    />
                  ) : (
                    <BtnSecondaryM
                      buttonCTA={`${copy.card_letter.button} ${selectedYear}`}
                      onClickLink={() => handleReadClickLetter(letterItem)}
                    />
                  )}
                </div>
              </Card>
            );
          })
        ) : (
          <Card
            iconTop={TbFileText}
            title={copy.card_letter.headline}
                          description={`${copy.card_letter.description} ${selectedYear}`}
            startButton={copy.card_letter.add_button}
            lottieAnimation={animation2}
            onClick={() => openPopup("letter")}
            options={addLetterDropdown}
            className="justify-center relative p-4"
            cardClassName="h-full"
          />
        ))}

      <Popup
        isOpen={popupType === "letter"}
        onClose={closePopup}
        title={copy.card_letter.popup.add_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={() => formRef.current?.requestSubmit()}
      >
        <form
          id="letter-form"
          ref={formRef}
          onSubmit={handleSubmit(handleSubmitLetter)}
          className="flex flex-col gap-3"
        >
          <InputText
            label={copy.card_letter.form.label_title}
            placeholder={copy.card_letter.form.placeholder_title}
            {...register("title_letter")}
            error={errors.title_letter?.message}
            details={`${watch("title_letter")?.length || 0}/200 characters`}
          />
          <InputTextarea
            label={copy.card_letter.form.label_text}
            placeholder={copy.card_letter.form.placeholder_text}
            {...register("letter")}
            error={errors.letter?.message}
            details={`${watch("letter")?.length || 0}/5000 characters`}
          />
        </form>
      </Popup>

      <Popup
        isOpen={popupType === "written-letter"}
        onClose={closePopup}
        title={copy.card_letter.popup.view_title}
        buttonSecondaryCTA={copy.dropdown.edit}
        buttonPrimaryCTA={copy.dropdown.cancel}
        onClickPrimaryLink={closePopup}
        onClickSecondaryLink={() => {
          if (!selectedLetter) return;
          setLetterToEdit(selectedLetter.id);
          setEditedLetterTitle(selectedLetter.title_letter);
          setEditedLetterText(selectedLetter.letter);
          setPopupType("letter-edit");
        }}
      >
        {selectedLetter && <LetterItem letterItem={selectedLetter} />}
      </Popup>

      <Popup
        isOpen={popupType === "letter-delete"}
        onClose={closePopup}
        title={copy.card_letter.popup.delete_title}
        description={copy.card_letter.popup.delete_description}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.delete}
        onClickPrimaryLink={handleDeleteLetter}
      />

      <Popup
        isOpen={popupType === "letter-edit"}
        onClose={closePopup}
        title={copy.card_letter.popup.edit_title}
        buttonSecondaryCTA={copy.dropdown.cancel}
        onClickSecondaryLink={closePopup}
        buttonPrimaryCTA={copy.dropdown.save}
        onClickPrimaryLink={handleEditLetter}
      >
        <div className="flex flex-col gap-4">
          <InputText
            label={copy.card_letter.form.label_title}
            placeholder={copy.card_letter.form.placeholder_title}
            value={editedLetterTitle}
            onChange={(e) => setEditedLetterTitle(e.target.value)}
            error={editErrors.title_letter}
            details={`${editedLetterTitle.length}/200 characters`}
          />
          <InputTextarea
            label={copy.card_letter.form.label_text}
            placeholder={copy.card_letter.form.placeholder_text}
            value={editedLetterText}
            onChange={(e) => setEditedLetterText(e.target.value)}
            error={editErrors.letter}
            details={`${editedLetterText.length}/5000 characters`}
          />
        </div>
      </Popup>
    </>
  );
};

export default CardLetter;
